// notificationSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpService from "@shared/services/http.service";
import { API_CONFIG } from "@shared/constants/api";
import { generateFcmToken } from "@/shared/services/firebase";
import { notify } from "@/shared/tostNotification/notify";
import { setDeviceToken } from "./authSlice";

// Types
interface Notification {
  id: string;
  title: string;
  message: string;
  type?: "info" | "success" | "warning" | "error";
  isRead: boolean;
  createdAt: string;
  [key: string]: any;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
}

interface NotificationState {
  data: Notification[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  pagination: PaginationData;
}

interface HeaderNotificationState {
  data: Notification[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

interface OptimisticUpdate {
  notification: Notification;
  timestamp: number;
}

export interface NotificationSliceState {
  receivedNotifications: NotificationState;
  sentNotifications: NotificationState;
  headerNotifications: HeaderNotificationState;
  unreadCount: number;
  isNotificationEnabled: boolean;
  // Optimistic update tracking
  optimisticUpdates: Record<string, OptimisticUpdate>;
  readingInProgress: string[];
  loading: boolean;
}

// Initial state
const initialState: NotificationSliceState = {
  receivedNotifications: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
    },
  },
  sentNotifications: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
    },
  },
  headerNotifications: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  unreadCount: 0,
  loading: false,
  isNotificationEnabled: false,
  optimisticUpdates: {},
  readingInProgress: [],
};

// Async thunks

/**
 * Fetch notifications with pagination (for all/sent tabs)
 */
export const fetchNotifications = createAsyncThunk(
  "notification/fetchNotifications",
  async (params: { tab: "all" | "sent"; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const { tab, page = 1, limit = 20 } = params;
      const response = await httpService.get(API_CONFIG.path.notification_list, {
        tab,
        page,
        limit,
      });

      return {
        tab,
        notifications: response.data || [],
        pagination: response.pagination || { page, limit, total: 0 },
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch notifications");
    }
  }
);

/**
 * Fetch header notifications (without pagination)
 */
export const fetchHeaderNotifications = createAsyncThunk(
  "notification/fetchHeaderNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpService.get(API_CONFIG.path.notification_list, {
        tab: "header",
      });

      return {
        notifications: response.data || [],
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch header notifications");
    }
  }
);

/**
 * Mark single notification as read with optimistic update
 */
export const markNotificationAsRead = createAsyncThunk(
  "notification/markAsRead",
  async ({ id, requestId }: { id: string; requestId: string }, { rejectWithValue }) => {
    try {
      // console.log(`[API] Making read request for notification ${id}`);
      await httpService.post(`${API_CONFIG.path.notification_read}`, {ids:[id]});
      // console.log(`[API] Successfully marked ${id} as read`);

      return { id, requestId };
    } catch (error: any) {
      console.error(`[API] Failed to mark ${id} as read:`, error);
      return rejectWithValue({
        notificationId: id,
        requestId,
        error: error.message || "Failed to mark notification as read",
        skipRollback: false,
      });
    }
  }
);

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = createAsyncThunk(
  "notification/markAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      await httpService.post(`${API_CONFIG.path.notification_read}`, { id: "all" });
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to mark all notifications as read");
    }
  }
);

/**
 * Delete notifications
 */
export const deleteNotifications = createAsyncThunk(
  "notification/delete",
  async ({ ids, tab }: { ids: string[]; tab: "all" | "sent" }, { rejectWithValue }) => {
    try {
      await httpService.deleteRequest(API_CONFIG.path.notification_delete, { ids, tab });
      return { ids, tab };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete notifications");
    }
  }
);

export const toggleNotificationsThunk = createAsyncThunk<void, boolean>(
  "notification/toggle",
  async (checked, { dispatch, getState }) => {
    const state: any = getState();
    const deviceToken = state.auth?.user?.device_token;

    /**
     * USER TURNS TOGGLE OFF
     * ---------------------
     */
    if (!checked) {
      dispatch(setNotificationEnabled(false));
      dispatch(setDeviceToken(""));

      await httpService.put(API_CONFIG.path.updateDeviceToken, {
        device_token: deviceToken,
        device_type: "w",
        is_notification_on: false,
      });

      notify("Notifications disabled", "warning");
      return;
    }

    /**
     * USER TURNS TOGGLE ON
     * -------------------
     */

    // 🔴 Permission already blocked → no popup allowed
    if (Notification.permission === "denied") {
      notify(
        "Notifications are blocked in your browser.\nChrome → 🔒 → Site settings → Notifications → Allow",
        "warning"
      );

      dispatch(setNotificationEnabled(false));
      dispatch(setDeviceToken(""));
      return;
    }

    // 🟡 Permission not decided yet → ask user
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        dispatch(setNotificationEnabled(false));
        return;
      }
    }

    /**
     * 🟢 Permission granted at this point
     */
    const token = await generateFcmToken();

    if (!token) {
      notify("Unable to enable notifications", "error");
      dispatch(setNotificationEnabled(false));
      return;
    }

    await httpService.put(API_CONFIG.path.updateDeviceToken, {
      device_token: token,
      device_type: "w",
      is_notification_on: true,
    });

    dispatch(setNotificationEnabled(true));
    dispatch(setDeviceToken(token));

    notify("Notifications enabled", "success");
  }
);

export const syncNotificationPermission = createAsyncThunk(
  "notification/syncPermission",
  async (_, { dispatch, getState }) => {
    const state: any = getState();

    const isEnabled = state.notification.isNotificationEnabled;
    const deviceToken = state.auth?.user?.device_token;
    const permission = Notification.permission;

    /**
     * CASE 1️⃣
     * Toggle ON but permission revoked → FORCE OFF
     */
    if (isEnabled && permission !== "granted") {
      dispatch(setNotificationEnabled(false));
      dispatch(setDeviceToken(""));

      await httpService.put(API_CONFIG.path.updateDeviceToken, {
        device_token: deviceToken,
        device_type: "w",
        is_notification_on: false,
      });

      notify("Notifications were disabled from browser settings", "warning");
      return;
    }

    /**
     * CASE 2️⃣ (NEW)
     * Toggle OFF + permission GRANTED + token missing → AUTO RECOVER
     */
    if (!isEnabled && permission === "granted" && !deviceToken) {
      const token = await generateFcmToken();

      if (!token) {
        return;
      }

      await httpService.put(API_CONFIG.path.updateDeviceToken, {
        device_token: token,
        device_type: "w",
        is_notification_on: true,
      });

      dispatch(setDeviceToken(token));
      dispatch(setNotificationEnabled(true));
    }

    /**
     * CASE 3️⃣ (NEW)
     * Toggle OFF + permission DENIED + token exists → REMOVE TOKEN
     */
    if (!isEnabled && permission !== "granted" && deviceToken) {
      await httpService.put(API_CONFIG.path.updateDeviceToken, {
        device_token: deviceToken,
        device_type: "w",
        is_notification_on: false,
      });

      dispatch(setDeviceToken(""));
    }
  }
);

// Notification slice
const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    /**
     * Add Incoming Notification
     */
    addNotification: (state, action: PayloadAction<any>) => {
      const fcmPayload = action.payload;

      // ---- isRead calculation ----
      const rawIsAdminRead = fcmPayload.data?.is_admin_read;
      const isRead = rawIsAdminRead === undefined ? false : String(rawIsAdminRead) === "true";

      // ---- Notification object ----
      const notification: Notification = {
        id: fcmPayload?.messageId || Date.now().toString(),
        title: fcmPayload?.notification?.title || fcmPayload?.data?.title || "New Notification",
        message: fcmPayload?.notification?.body || fcmPayload?.data?.message || "",
        type: fcmPayload?.data?.tag || "info",
        isRead,
        createdAt: new Date().toISOString(),
        ...fcmPayload?.data,
      };

      // ---- Add to received notifications ----
      state.receivedNotifications.data.unshift(notification);
      state.receivedNotifications.pagination.total += 1;

      // ---- Header notification logic ----
      if (!notification.isRead) {
        state.headerNotifications.data.unshift(notification);

        if (state.headerNotifications.data.length > 20) {
          state.headerNotifications.data.pop();
        }

        state.unreadCount += 1;
      }
    },

    /**
     * Optimistic update: Remove notification immediately from header
     */
    markAsReadOptimistic: (state, action: PayloadAction<{ id: string; requestId: string }>) => {
      const { id, requestId } = action.payload;

      // Find notification in header
      const headerIndex = state.headerNotifications.data.findIndex((n) => n.id === id);

      if (headerIndex === -1) {
        console.warn(`[Optimistic] Notification ${id} not found in header`);
        return;
      }

      const notification = state.headerNotifications.data[headerIndex];

      // Skip if already read
      if (notification.isRead) {
        // console.log(`[Optimistic] Notification ${id} already marked as read`);
        return;
      }

      // Store backup for potential rollback
      state.optimisticUpdates[requestId] = {
        notification: { ...notification },
        timestamp: Date.now(),
      };

      // Track that we're reading this notification
      if (!state.readingInProgress.includes(id)) {
        state.readingInProgress.push(id);
      }

      // Remove from header immediately (optimistic)
      state.headerNotifications.data.splice(headerIndex, 1);

      // Decrement unread count
      state.unreadCount = Math.max(0, state.unreadCount - 1);

      // console.log( `[Optimistic] Removed notification ${id} from header, unread: ${state.unreadCount}`);
    },

    /**
     * Rollback optimistic update on failure
     */
    rollbackOptimisticUpdate: (state, action: PayloadAction<string>) => {
      const requestId = action.payload;
      const backup = state.optimisticUpdates[requestId];

      if (!backup) {
        console.warn(`[Rollback] No backup found for request ${requestId}`);
        return;
      }

      const notification = backup.notification;

      // Restore to header if it was unread
      if (!notification.isRead) {
        // Check if notification is not already in header (avoid duplicates)
        const existsInHeader = state.headerNotifications.data.some((n) => n.id === notification.id);

        if (!existsInHeader) {
          state.headerNotifications.data.unshift(notification);
          state.unreadCount += 1;
          // console.log(`[Rollback] Restored notification ${notification.id} to header`);
        }
      }

      // Remove from reading in progress
      state.readingInProgress = state.readingInProgress.filter((id) => id !== notification.id);

      // Clean up backup
      delete state.optimisticUpdates[requestId];
    },

    /**
     * Clean up successful optimistic update
     */
    cleanupOptimisticUpdate: (state, action: PayloadAction<string>) => {
      const requestId = action.payload;
      const backup = state.optimisticUpdates[requestId];

      if (backup) {
        // Remove from reading in progress
        state.readingInProgress = state.readingInProgress.filter(
          (id) => id !== backup.notification.id
        );

        // Clean up backup
        delete state.optimisticUpdates[requestId];

        // console.log(`[Cleanup] Cleaned up request ${requestId}`);
      }
    },

    /**
     * Reset notification state
     */
    resetNotificationState: () => initialState,

    /**
     * Update notification locally
     */
    updateNotificationLocally: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Notification> }>
    ) => {
      const { id, updates } = action.payload;

      const receivedIndex = state.receivedNotifications.data.findIndex((n) => n.id === id);
      if (receivedIndex !== -1) {
        state.receivedNotifications.data[receivedIndex] = {
          ...state.receivedNotifications.data[receivedIndex],
          ...updates,
        };
      }

      const sentIndex = state.sentNotifications.data.findIndex((n) => n.id === id);
      if (sentIndex !== -1) {
        state.sentNotifications.data[sentIndex] = {
          ...state.sentNotifications.data[sentIndex],
          ...updates,
        };
      }

      const headerIndex = state.headerNotifications.data.findIndex((n) => n.id === id);
      if (headerIndex !== -1) {
        state.headerNotifications.data[headerIndex] = {
          ...state.headerNotifications.data[headerIndex],
          ...updates,
        };
      }
    },

    setNotificationEnabled(state, action: PayloadAction<boolean>) {
      state.isNotificationEnabled = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch notifications (all/sent with pagination)
      .addCase(fetchNotifications.pending, (state, action) => {
        const tab = action.meta.arg.tab;
        if (tab === "all") {
          state.receivedNotifications.loading = true;
          state.receivedNotifications.error = null;
        } else {
          state.sentNotifications.loading = true;
          state.sentNotifications.error = null;
        }
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { tab, notifications, pagination } = action.payload;

        if (tab === "all") {
          state.receivedNotifications.loading = false;
          state.receivedNotifications.data = notifications;
          state.receivedNotifications.pagination = pagination;
          state.receivedNotifications.lastFetched = Date.now();
        } else {
          state.sentNotifications.loading = false;
          state.sentNotifications.data = notifications;
          state.sentNotifications.pagination = pagination;
          state.sentNotifications.lastFetched = Date.now();
        }
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        const tab = action.meta.arg.tab;
        if (tab === "all") {
          state.receivedNotifications.loading = false;
          state.receivedNotifications.error = action.payload as string;
        } else {
          state.sentNotifications.loading = false;
          state.sentNotifications.error = action.payload as string;
        }
      })

      // Fetch header notifications
      .addCase(fetchHeaderNotifications.pending, (state) => {
        state.headerNotifications.loading = true;
        state.headerNotifications.error = null;
      })
      .addCase(fetchHeaderNotifications.fulfilled, (state, action) => {
        state.headerNotifications.loading = false;
        state.headerNotifications.data = action.payload.notifications;
        state.headerNotifications.lastFetched = Date.now();

        state.unreadCount = action.payload.notifications.filter(
          (n: Notification) => !n.isRead
        ).length;
      })
      .addCase(fetchHeaderNotifications.rejected, (state, action) => {
        state.headerNotifications.loading = false;
        state.headerNotifications.error = action.payload as string;
      })

      // Mark notification as read - SUCCESS
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { id, requestId } = action.payload;

        // Clean up optimistic update tracking
        const backup = state.optimisticUpdates[requestId];
        if (backup) {
          delete state.optimisticUpdates[requestId];
        }

        // Remove from reading in progress
        state.readingInProgress = state.readingInProgress.filter((nid) => nid !== id);

        // Update in received notifications
        const receivedNotif = state.receivedNotifications.data.find((n) => n.id === id);
        if (receivedNotif && !receivedNotif.isRead) {
          receivedNotif.isRead = true;
        }

        // Update in sent notifications
        const sentNotif = state.sentNotifications.data.find((n) => n.id === id);
        if (sentNotif && !sentNotif.isRead) {
          sentNotif.isRead = true;
        }

        // console.log(`[Success] Marked notification ${id} as read`);
      })

      // Mark notification as read - FAILURE
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        const payload = action.payload as any;
        const { notificationId, requestId, skipRollback } = payload;

        // Skip rollback for duplicate requests
        if (skipRollback) {
          // console.log(`[Skipped] Rollback skipped for notification ${notificationId}`);
          return;
        }

        // Rollback the optimistic update
        const backup = state.optimisticUpdates[requestId];
        if (backup) {
          const notification = backup.notification;

          // Restore to header if unread
          if (!notification.isRead) {
            const existsInHeader = state.headerNotifications.data.some(
              (n) => n.id === notification.id
            );

            if (!existsInHeader) {
              state.headerNotifications.data.unshift(notification);
              state.unreadCount += 1;
            }
          }

          delete state.optimisticUpdates[requestId];
          // console.log(`[Rollback] Failed to mark ${notificationId} as read, restored to header`);
        }

        // Remove from reading in progress
        state.readingInProgress = state.readingInProgress.filter((id) => id !== notificationId);
      })

      // Mark all notifications as read
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.receivedNotifications.data.forEach((n) => {
          n.isRead = true;
        });

        state.sentNotifications.data.forEach((n) => {
          n.isRead = true;
        });

        state.headerNotifications.data.forEach((n) => {
          n.isRead = true;
        });

        state.unreadCount = 0;
      })

      // Delete notifications
      .addCase(deleteNotifications.fulfilled, (state, action) => {
        const { ids, tab } = action.payload;

        if (tab === "all") {
          ids.forEach((id) => {
            const index = state.receivedNotifications.data.findIndex((n) => n.id === id);
            if (index !== -1) {
              const notification = state.receivedNotifications.data[index];
              if (!notification.isRead) {
                state.unreadCount = Math.max(0, state.unreadCount - 1);
              }
              state.receivedNotifications.data.splice(index, 1);
              state.receivedNotifications.pagination.total = Math.max(
                0,
                state.receivedNotifications.pagination.total - 1
              );
            }
          });

          ids.forEach((id) => {
            const headerIndex = state.headerNotifications.data.findIndex((n) => n.id === id);
            if (headerIndex !== -1) {
              state.headerNotifications.data.splice(headerIndex, 1);
            }
          });
        } else {
          ids.forEach((id) => {
            const index = state.sentNotifications.data.findIndex((n) => n.id === id);
            if (index !== -1) {
              state.sentNotifications.data.splice(index, 1);
              state.sentNotifications.pagination.total = Math.max(
                0,
                state.sentNotifications.pagination.total - 1
              );
            }
          });
        }
      })

      // Toggle Loading
      .addCase(toggleNotificationsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleNotificationsThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(toggleNotificationsThunk.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export actions
export const {
  addNotification,
  markAsReadOptimistic,
  rollbackOptimisticUpdate,
  cleanupOptimisticUpdate,
  resetNotificationState,
  updateNotificationLocally,
  setNotificationEnabled,
} = notificationSlice.actions;

export default notificationSlice.reducer;
